import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { CATEGORIES, PRODUCTS } from "@/data/products";

const EASE = [0.16, 1, 0.3, 1];

function ProductCard({ product, index, onRequestQuote }) {
  return (
    <motion.article
      layout
      data-testid={`product-card-${product.id}`}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97 }}
      transition={{ duration: 0.5, delay: index * 0.05, ease: EASE }}
      whileHover={{ scale: 1.02 }}
      className="group relative border border-zinc-200 bg-white"
    >
      <div className="relative aspect-[4/5] overflow-hidden bg-tour-zinc">
        <img
          src={product.img}
          alt={product.name}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
        <div
          className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
          style={{ background: "radial-gradient(circle at 50% 30%, rgba(255,255,255,0.35), transparent 60%)" }}
        />
        <span className="absolute left-4 top-4 bg-tour-void px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-white">
          {product.category}
        </span>
      </div>
      <div className="p-5">
        <h3 className="font-display text-base font-bold tracking-tight">{product.name}</h3>
        <div className="mt-3 space-y-1.5 font-mono text-[11px] uppercase tracking-wider text-zinc-500">
          <p>{product.spec}</p>
          <p>{product.technique}</p>
        </div>
        <div className="mt-5 flex items-center justify-between border-t border-zinc-100 pt-4">
          <span className="font-mono text-[11px] tracking-widest text-zinc-400">{product.moq}</span>
          <button
            data-testid={`product-quote-btn-${product.id}`}
            onClick={() => onRequestQuote(product.category)}
            className="flex items-center gap-1 font-mono text-[11px] uppercase tracking-widest text-tour-void transition-colors hover:text-tour-red"
          >
            Request quote
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>
      </div>
    </motion.article>
  );
}

export default function Catalog({ onRequestQuote }) {
  const [active, setActive] = useState("All");
  const filtered = active === "All" ? PRODUCTS : PRODUCTS.filter((p) => p.category === active);

  return (
    <section id="catalog" data-testid="product-catalog-section" className="border-t border-zinc-200 bg-tour-zinc/60 py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: EASE }}
          className="mb-12 flex flex-col justify-between gap-6 md:flex-row md:items-end"
        >
          <div className="max-w-xl">
            <p className="mb-4 font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">The catalog</p>
            <h2 className="font-display text-3xl font-extrabold tracking-tighter sm:text-4xl lg:text-5xl">
              Built for the <span className="font-serif italic font-medium">merch table.</span>
            </h2>
            <p className="mt-4 text-sm text-zinc-600 sm:text-base">
              Wholesale blanks, tour-grade print techniques, no listed prices — every run is quoted to your routing and quantity.
            </p>
          </div>
        </motion.div>

        <div data-testid="catalog-filter-tabs" className="mb-10 flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`filter-tab-${c.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setActive(c)}
              className={`relative px-4 py-2 font-mono text-[11px] uppercase tracking-widest transition-colors ${
                active === c ? "text-white" : "text-zinc-600 hover:text-black"
              }`}
            >
              {active === c && (
                <motion.span
                  layoutId="filter-pill"
                  transition={{ type: "spring", stiffness: 400, damping: 32 }}
                  className="absolute inset-0 bg-tour-void"
                />
              )}
              <span className="relative z-10">{c}</span>
            </button>
          ))}
        </div>

        <motion.div layout className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {filtered.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} onRequestQuote={onRequestQuote} />
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}
